import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { motion } from 'framer-motion';
import { Quote, MessageCircle } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/Card';

const testimonials = [
  {
    quote: "The AI coach doesn't just give generic advice—it knows my goals and my learning path. I've seen real progress in how I communicate with stakeholders.",
    name: 'Alex K.',
    role: 'Product Manager',
    gradient: 'from-primary/10 to-violet-500/10',
    iconBg: 'bg-primary/15 text-primary',
  },
  {
    quote: "Micro learning fits my schedule. I do one lesson with my coffee and the nudges keep me accountable. My manager noticed the change in how I run meetings.",
    name: 'Sam T.',
    role: 'Engineering Lead',
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
  {
    quote: "I was skeptical about AI for soft skills. Five AI proved me wrong—the feedback is specific and actionable. Best investment in my career development.",
    name: 'Jordan L.',
    role: 'Director, Operations',
    gradient: 'from-amber-500/10 to-orange-500/10',
    iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  },
];

export const TestimonialsPage: React.FC = () => {
  useSEO({ title: 'Testimonials - How Professionals Use Five AI', description: 'See how product managers, engineering leads, directors, and analysts use Five AI to improve communication, networking, and leadership skills with personalized AI coaching.', path: '/testimonials' });

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Testimonials">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-4xl mx-auto">
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <MessageCircle className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">What people say</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Real stories,</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">real growth</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                Hear from learners who use Five AI to grow their careers with AI coaching and personalized learning.
              </p>
              <p className="text-sm text-muted-foreground/80">
                Testimonials
              </p>
            </motion.div>
          </div>
        </section>
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card
                    hover
                    className={`h-full p-6 sm:p-8 rounded-2xl border-2 bg-gradient-to-br ${t.gradient} hover:border-primary/40 transition-all duration-300`}
                  >
                    <div className={`w-11 h-11 rounded-xl ${t.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                      <Quote className="w-5 h-5" />
                    </div>
                    <blockquote className="text-foreground mb-4 italic leading-relaxed">"{t.quote}"</blockquote>
                    <p className="font-semibold text-foreground">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.role}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
